import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail, imageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/LangContext";
import { pickLocalized, labelType, labelCondition } from "@/lib/i18n";
import Navbar from "@/components/Navbar";
import HeritageForm from "@/components/HeritageForm";
import DeleteDialog from "@/components/DeleteDialog";
import { toast } from "sonner";
import {
  Buildings, MapPin, Shield, Plus, PencilSimple, Trash, Eye, DownloadSimple,
} from "@phosphor-icons/react";

export default function AdminDashboard() {
  const { user } = useAuth();
  const { lang, t } = useI18n();
  const [stats, setStats] = useState({ total: 0, by_authority: [], by_type: [], by_condition: [] });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, list] = await Promise.all([api.get("/stats"), api.get("/heritage")]);
      setStats(s.data);
      setItems(list.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("toast.load.failed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const confirmDelete = async () => {
    if (!deleteItem) return;
    try {
      await api.delete(`/heritage/${deleteItem.id}`);
      toast.success(t("toast.deleted"));
      setDeleteItem(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("toast.delete.failed"));
    }
  };

  const openAdd = () => { setEditItem(null); setFormOpen(true); };

  return (
    <div className="min-h-screen bg-[#f9f8f6]">
      <Navbar />

      <section className="border-b border-[#e5e3db] bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <div className="overline mb-3">{t("admin.overline")}</div>
            <h1 className="serif text-4xl lg:text-5xl font-black tracking-tight text-[#1a1a1a] leading-none">
              {t("admin.title", { name: user?.name || "Admin" })}
            </h1>
            <p className="mt-3 text-sm text-[#5c5c5c] max-w-xl">{t("admin.subtitle")}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3 self-start md:self-auto">
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/heritage/export/csv`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 h-11 px-4 border border-[#e5e3db] bg-white text-sm uppercase tracking-[0.16em] font-semibold text-[#1a1a1a] hover:bg-[#1a1a1a] hover:text-white hover:border-[#1a1a1a] transition-colors"
              data-testid="admin-csv-export-btn"
            >
              <DownloadSimple size={16} weight="duotone" />
              {t("admin.export")}
            </a>
            <button
              onClick={openAdd}
              className="inline-flex items-center gap-2 h-11 px-5 bg-[#b25a38] text-white text-sm uppercase tracking-[0.16em] font-semibold hover:bg-[#93492c] transition-colors"
              data-testid="admin-add-btn"
            >
              <Plus size={16} weight="bold" />
              {t("admin.new")}
            </button>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 space-y-10">
        <div className="grid grid-cols-1 md:grid-cols-4 border border-[#e5e3db] bg-white" data-testid="stats-grid">
          <BigStat label={t("admin.stat.total")} value={stats.total} icon={<Buildings size={20} weight="duotone" />} testid="stat-total" />
          <BigStat label={t("admin.stat.authorities")} value={stats.by_authority.length} icon={<Shield size={20} weight="duotone" />} testid="stat-authorities" />
          <BigStat label={t("admin.stat.types")} value={stats.by_type.length} icon={<MapPin size={20} weight="duotone" />} testid="stat-types" />
          <BigStat
            label={t("admin.stat.risk")}
            value={stats.by_condition
              .filter((c) => ["Poor", "Critical"].includes(c.name))
              .reduce((s, c) => s + c.count, 0)}
            icon={<Shield size={20} weight="duotone" />}
            highlight
            testid="stat-at-risk"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Breakdown title={t("admin.breakdown.authority")} rows={stats.by_authority} testid="breakdown-authority" />
          <Breakdown title={t("admin.breakdown.type")} rows={stats.by_type.map((r) => ({ ...r, name: labelType(r.name, lang) }))} testid="breakdown-type" />
        </div>

        <div className="border border-[#e5e3db] bg-white">
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#e5e3db]">
            <div>
              <div className="overline">{t("admin.records.overline")}</div>
              <h2 className="serif text-xl font-bold text-[#1a1a1a]">{t("admin.records.title")}</h2>
            </div>
            <Link
              to="/"
              className="text-xs uppercase tracking-[0.18em] font-semibold text-[#b25a38] hover:text-[#93492c] flex items-center gap-1"
              data-testid="admin-browse-link"
            >
              <Eye size={14} weight="duotone" /> {t("admin.records.public")}
            </Link>
          </div>

          {loading ? (
            <div className="p-6 text-sm text-[#5c5c5c]" data-testid="admin-loading">{t("admin.records.loading")}</div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center">
              <div className="serif text-2xl text-[#1a1a1a] mb-1">{t("admin.records.empty.title")}</div>
              <p className="text-sm text-[#5c5c5c]">{t("admin.records.empty.body")}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-records-table">
                <thead className="bg-[#faf8f4] border-b border-[#e5e3db]">
                  <tr className="text-left text-[10px] uppercase tracking-[0.2em] font-semibold text-[#5c5c5c]">
                    <th className="px-6 py-3">{t("admin.col.record")}</th>
                    <th className="px-6 py-3">{t("admin.col.type")}</th>
                    <th className="px-6 py-3">{t("admin.col.authority")}</th>
                    <th className="px-6 py-3">{t("admin.col.year")}</th>
                    <th className="px-6 py-3">{t("admin.col.condition")}</th>
                    <th className="px-6 py-3 text-right">{t("admin.col.actions")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e5e3db]">
                  {items.map((it) => {
                    const name = pickLocalized(it, "name", lang);
                    const location = pickLocalized(it, "location", lang);
                    return (
                      <tr key={it.id} className="hover:bg-[#faf8f4]" data-testid={`admin-row-${it.id}`}>
                        <td className="px-6 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-11 h-11 bg-[#efece4] border border-[#e5e3db] overflow-hidden flex-shrink-0">
                              {(it.image_urls?.[0] || it.image_url) && (
                                <img src={imageUrl(it.image_urls?.[0] || it.image_url)} alt="" className="w-full h-full object-cover" />
                              )}
                            </div>
                            <div className="min-w-0">
                              <div className="serif font-bold text-[#1a1a1a] truncate">{name}</div>
                              <div className="text-xs text-[#5c5c5c] truncate">{location || "—"}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-3 text-[#1a1a1a]">{labelType(it.type, lang)}</td>
                        <td className="px-6 py-3 text-[#1a1a1a]">{it.local_authority || "—"}</td>
                        <td className="px-6 py-3 text-[#1a1a1a]">{it.year_built || "—"}</td>
                        <td className="px-6 py-3">
                          <span className="text-[10px] uppercase tracking-[0.18em] font-semibold px-2 py-1 border border-[#e5e3db] text-[#1a1a1a] bg-[#faf8f4]">
                            {labelCondition(it.condition, lang)}
                          </span>
                        </td>
                        <td className="px-6 py-3">
                          <div className="flex items-center gap-1 justify-end">
                            <button
                              onClick={() => { setEditItem(it); setFormOpen(true); }}
                              className="h-8 w-8 flex items-center justify-center border border-transparent text-[#5c5c5c] hover:text-[#b25a38] hover:border-[#e5e3db] transition-colors"
                              data-testid={`admin-edit-${it.id}`}
                              aria-label="Edit"
                            >
                              <PencilSimple size={15} weight="duotone" />
                            </button>
                            <button
                              onClick={() => setDeleteItem(it)}
                              className="h-8 w-8 flex items-center justify-center border border-transparent text-[#5c5c5c] hover:text-[#d32f2f] hover:border-[#e5e3db] transition-colors"
                              data-testid={`admin-delete-${it.id}`}
                              aria-label="Delete"
                            >
                              <Trash size={15} weight="duotone" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <HeritageForm open={formOpen} onOpenChange={setFormOpen} initial={editItem} onSaved={load} />
      <DeleteDialog open={!!deleteItem} onOpenChange={(o) => !o && setDeleteItem(null)} item={deleteItem} onConfirm={confirmDelete} />
    </div>
  );
}

function BigStat({ label, value, icon, highlight, testid }) {
  return (
    <div
      className={`p-6 lg:p-8 border-r border-b border-[#e5e3db] last:border-r-0 ${
        highlight ? "bg-[#fbeee0]" : "bg-white"
      }`}
      data-testid={testid}
    >
      <div className={highlight ? "text-[#8a5a1e]" : "text-[#b25a38]"}>{icon}</div>
      <div className="serif text-4xl lg:text-5xl font-black text-[#1a1a1a] leading-none mt-4">{value}</div>
      <div className="overline mt-3">{label}</div>
    </div>
  );
}

function Breakdown({ title, rows, testid }) {
  const total = rows.reduce((s, r) => s + r.count, 0) || 1;
  return (
    <div className="border border-[#e5e3db] bg-white p-6 lg:p-8" data-testid={testid}>
      <div className="overline mb-3">{title}</div>
      {rows.length === 0 ? (
        <div className="text-sm text-[#5c5c5c]">—</div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => {
            const pct = Math.round((r.count / total) * 100);
            return (
              <div key={r.name}>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#1a1a1a]">{r.name}</span>
                  <span className="font-mono text-[#5c5c5c] text-xs">{r.count} · {pct}%</span>
                </div>
                <div className="mt-1.5 h-1 bg-[#efece4] overflow-hidden">
                  <div className="h-full bg-[#b25a38]" style={{ width: `${pct}%`, transition: "width 300ms ease" }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
